import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import { DIETARY_OPTIONS, GUEST_TYPES } from "@/lib/guests";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const RSVP_OPTIONS = ["Attending", "Pending", "Declined"];

export const BLANK_GUEST = {
  name: "",
  type: "adult",
  table_number: "",
  dietary: "none",
  dietary_notes: "",
  email: "",
  phone: "",
  group: "",
  rsvp: "Pending",
  notes: "",
};

// Shared by the client Guests page and the staff event planning section.
export function GuestFormDialog({ open, onOpenChange, editing, form, setForm, onSave, saving }) {
  const [more, setMore] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="guest-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl text-primary">
            {editing ? "Edit Guest" : "Add Guest"}
          </DialogTitle>
          <DialogDescription>
            Name, type, table and dietary requirement. Extra contact details are optional.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <Label className="eyebrow">Name</Label>
            <Input
              className="mt-2"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder="Full name"
              data-testid="guest-name-input"
            />
          </div>
          <div>
            <Label className="eyebrow">Type</Label>
            <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Guest type">
              {GUEST_TYPES.map((type) => (
                <PillOption
                  key={type.id}
                  active={form.type === type.id}
                  onClick={() => setForm({ ...form, type: type.id })}
                  testid={`guest-modal-type-${type.id}`}
                >
                  {type.label}
                </PillOption>
              ))}
            </div>
          </div>
          <div>
            <Label className="eyebrow">Table number</Label>
            <Input
              className="mt-2"
              value={form.table_number}
              onChange={(event) => setForm({ ...form, table_number: event.target.value })}
              placeholder="e.g. 5 or Head Table"
              data-testid="guest-table-number-input"
            />
          </div>
          <div>
            <Label className="eyebrow">Dietary requirement</Label>
            <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Dietary requirement">
              {DIETARY_OPTIONS.map((option) => (
                <PillOption
                  key={option.id}
                  active={form.dietary === option.id}
                  onClick={() => setForm({ ...form, dietary: option.id })}
                  testid={`guest-modal-diet-${option.id}`}
                  subtle
                >
                  {option.label}
                </PillOption>
              ))}
            </div>
          </div>
          {form.dietary === "other" && (
            <div>
              <Label className="eyebrow">Specify</Label>
              <Input
                className="mt-2"
                value={form.dietary_notes}
                onChange={(event) => setForm({ ...form, dietary_notes: event.target.value })}
                placeholder="e.g. shellfish allergy"
                data-testid="guest-modal-diet-notes"
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => setMore(!more)}
            className="flex items-center gap-2 text-sm text-secondary transition-colors duration-200 hover:text-primary"
            data-testid="guest-more-details-toggle"
            aria-expanded={more}
          >
            <CaretDown size={14} className={`transition-transform duration-300 ${more ? "rotate-180" : ""}`} />
            {more ? "Hide more details" : "More details (optional)"}
          </button>
          {more && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="guest-more-details">
              <div>
                <Label className="eyebrow">Email</Label>
                <Input className="mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="guest-email-input" />
              </div>
              <div>
                <Label className="eyebrow">Phone</Label>
                <Input className="mt-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="guest-phone-input" />
              </div>
              <div>
                <Label className="eyebrow">Group</Label>
                <Input className="mt-2" value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} placeholder="e.g. Bride's family" data-testid="guest-group-input" />
              </div>
              <div>
                <Label className="eyebrow">RSVP</Label>
                <Select value={form.rsvp} onValueChange={(value) => setForm({ ...form, rsvp: value })}>
                  <SelectTrigger className="mt-2" data-testid="guest-rsvp-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RSVP_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="eyebrow">Notes</Label>
                <Textarea className="mt-2" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="guest-notes-input" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="guest-modal-cancel">
            Cancel
          </Button>
          <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={onSave} disabled={saving} data-testid="save-guest-btn">
            {saving ? "Saving…" : editing ? "Save changes" : "Add guest"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PillOption({ active, onClick, children, testid, subtle }) {
  const activeClass = subtle
    ? "border-secondary bg-secondary/15 text-secondary"
    : "border-primary bg-primary text-primary-foreground";
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      data-testid={testid}
      className={`rounded-full border px-4 py-2 text-sm transition-colors duration-200 ${
        active ? activeClass : "border-border text-primary hover:bg-muted"
      }`}
    >
      {children}
    </button>
  );
}
